import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { activityApi } from '../services/api';
import { LeadActivity } from '../types';
import { fmtLongDay, fmtTime, istToday, shiftDay } from '../lib';
import { Empty, Failed, Loading, StageChip } from '../components/WorkBits';
import { CallIcon, WhatsAppIcon } from '../components/BrandIcons';
import { callFacts } from '../components/lead/leadModel';
import { RecordingCard, fmtDuration } from '../components/lead/Timeline';

/** The operator's calls, one IST day at a time — the Nurture app's Calls tab.
 *
 *  Built from the call activities core holds for this operator (the phone's
 *  observer posts every dial). A browser can't read the phone's call log, so
 *  calls from numbers that aren't leads stay on the phone. */

type Filter = 'all' | 'connected' | 'not_connected';

const connected = (a: LeadActivity) => {
  const f = callFacts(a);
  return f.connectionResult ? f.connectionResult === 'connected' : (f.durationSeconds || 0) > 0;
};

const recState = (a: LeadActivity) => {
  const recs = callFacts(a).recordings;
  if (!recs.length) return null;
  const scored = recs.find((r: any) => r.summary?.verdict?.overall_score != null);
  if (scored) return { cls: 'chip-forest', text: `Score ${scored.summary.verdict.overall_score}` };
  if (recs.some((r: any) => r.transcription?.status === 'failed')) return { cls: 'chip-brick', text: 'Transcript failed' };
  return { cls: 'chip-gold', text: 'Transcribing…' };
};

const CallsPage: React.FC = () => {
  const navigate = useNavigate();
  const [day, setDay] = useState(istToday());
  const [calls, setCalls] = useState<LeadActivity[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [open, setOpen] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setCalls(null);
    setError(null);
    activityApi.list({
      kind: 'call',
      occurred_from: new Date(`${day}T00:00:00+05:30`).toISOString(),
      occurred_to: new Date(`${shiftDay(day, 1)}T00:00:00+05:30`).toISOString(),
      limit: 1000,
    })
      .then((r) => { if (live) setCalls(r.activity); })
      .catch((err) => { if (live) setError(err?.response?.data?.detail || err.message); });
    return () => { live = false; };
  }, [day]);

  const isToday = day === istToday();
  const all = calls || [];
  const shown = all.filter((a) => filter === 'all' || (filter === 'connected') === connected(a));
  const talk = all.reduce((s, a) => s + (connected(a) ? callFacts(a).durationSeconds || 0 : 0), 0);
  const recorded = all.filter((a) => callFacts(a).recordings.length > 0).length;
  const scored = all.filter((a) => recState(a)?.cls === 'chip-forest').length;

  const STAT = (v: React.ReactNode, k: string, head = false) => (
    <div className="px-3 first:pl-0 border-l-[1.5px] border-dotted border-sketch first:border-l-0">
      <div className={`font-display text-[24px] font-semibold num leading-tight ${head ? 'text-forest' : ''}`}>{v}</div>
      <div className="text-[11.5px] text-graphite mt-0.5">{k}</div>
    </div>
  );

  return (
    <div className="w-full px-7 pt-6 pb-16">
      <div className="page-head">
        <div>
          <h1 className="page-title">Calls</h1>
          <p className="page-sub">Every call on the leads you are viewing, as Nurture recorded it · times IST</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="icb" aria-label="Previous day" onClick={() => setDay(shiftDay(day, -1))}>‹</button>
          <b className="font-display text-[14px] min-w-[190px] text-center">
            {isToday ? 'Today · ' : ''}{fmtLongDay(`${day}T12:00:00+05:30`)}
          </b>
          <button className="icb" aria-label="Next day" disabled={isToday} onClick={() => setDay(shiftDay(day, 1))}>›</button>
        </div>
      </div>

      {error && <Failed what="calls" error={error} />}
      {!error && !calls && <Loading what="calls" />}

      {calls && (
        <div className="card !px-0 !pb-1.5">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-y-3 px-5 pb-3">
            {STAT(all.length, 'Calls')}
            {STAT(all.filter(connected).length, 'Connected', true)}
            {STAT(fmtDuration(talk) || '0s', 'Talk time')}
            {STAT(recorded, 'Recordings in')}
            {STAT(scored, 'Scored')}
          </div>
          <div className="flex gap-1.5 px-5 pb-2.5">
            {([['all', 'All', all.length], ['connected', 'Connected', all.filter(connected).length],
              ['not_connected', 'Not connected', all.filter((a) => !connected(a)).length]] as const).map(([k, l, n]) => (
              <button key={k} className="pill" aria-pressed={filter === k} onClick={() => setFilter(k)}>
                {l} <span className="num opacity-70 ml-0.5">{n}</span>
              </button>
            ))}
          </div>

          {shown.length === 0 ? (
            <Empty>{all.length ? 'No calls of this kind.' : 'No calls on this day.'}</Empty>
          ) : shown.map((a) => {
            const f = callFacts(a);
            const rec = recState(a);
            const ok = connected(a);
            const expanded = open === a.id;
            const name = a.lead?.name?.trim() || a.lead?.mobile || 'Unknown lead';
            return (
              <div key={a.id} className="border-t border-dotted border-sketch-soft">
                <div
                  className="grid grid-cols-[36px_minmax(0,1.4fr)_minmax(0,1fr)_auto_auto] gap-3 items-center px-5 py-2.5 cursor-pointer hover:bg-cream"
                  onClick={() => setOpen(expanded ? null : a.id)}
                >
                  <span className="relative">
                    <span className="avat w-9 h-9 text-[13px]">{name.charAt(0).toUpperCase()}</span>
                    <span className="absolute -right-1 -bottom-1 w-[18px] h-[18px] rounded-full bg-warm-white border-[1.5px] border-sketch grid place-items-center">
                      {a.channel === 'whatsapp' ? <WhatsAppIcon className="w-3 h-3" /> : <CallIcon className="w-3 h-3" />}
                    </span>
                  </span>
                  <span className="min-w-0">
                    <button className="block text-[13.5px] font-semibold truncate hover:text-forest text-left"
                      onClick={(e) => { e.stopPropagation(); navigate(`/leads/${a.lead_id}`); }}>{name}</button>
                    <span className="block text-[11.5px] text-graphite">{a.direction === 'inbound' ? 'Incoming' : 'Outgoing'}</span>
                  </span>
                  <span className={`num text-[12.5px] ${ok ? '' : 'text-brick'}`}>
                    {fmtTime(a.occurred_at)} · {ok ? fmtDuration(f.durationSeconds) || 'connected' : (f.connectionResult || 'no answer').replace(/_/g, ' ')}
                  </span>
                  <StageChip stage={a.lead?.stage ?? null} />
                  <span className="flex items-center gap-2 justify-end min-w-[120px]">
                    {rec ? <span className={`chip ${rec.cls}`}>{rec.text}</span> : <span className="text-graphite text-[12px]">No recording</span>}
                    <span className="text-graphite w-3">{f.recordings.length ? (expanded ? '▾' : '▸') : ''}</span>
                  </span>
                </div>
                {expanded && f.recordings.length > 0 && (
                  <div className="px-5 pb-3 pl-[68px]">
                    {f.recordings.map((r: Record<string, any>, i: number) => <RecordingCard key={i} rec={r} />)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CallsPage;
